import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Spin, Button, Spacer } from '@brokerbay/design-components';
import { Button as ButtonV2, Color, Icon, Input } from '@bb/elements-web';
import { isValidEmail } from 'utils';
import {
  CloseCircleButtonStyle,
  DetailsContainer,
  EditContainer,
  EditTitleContainer,
  SaveButtonWidth,
  StyledBody,
  StyledDescription,
  StyledHeader,
} from './styledComponents';
import {
  CanManageType,
  BillingInvoicesPageContext,
} from '../../BillingInvoicesPageContext';
import { FormStatus } from '../FormStatus';

type BillingEmailCardProps = {
  canManageBilling: CanManageType;
};

const BillingEmailCard: React.FC<BillingEmailCardProps> = ({
  canManageBilling,
}) => {
  const {
    billingProfile,
    updateBillingProfile,
    isFetchingProfile,
    isUpdatingProfile,
    hasSavedCard,
  } = useContext(BillingInvoicesPageContext);

  const [updatedEmail, setUpdatedEmail] = useState('');
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [updateProfileError, setUpdateProfileError] = useState<Error>();

  const switchToEditMode = useCallback(() => {
    if (canManageBilling.response) {
      setUpdatedEmail(billingProfile?.Email || '');
      setIsInEditMode(true);
    }
  }, [canManageBilling, billingProfile?.Email]);

  const closeEditMode = useCallback(() => {
    setUpdatedEmail('');
    setIsInEditMode(false);
  }, []);

  const updateEmail = useCallback(() => {
    if (canManageBilling.response) {
      const updatedProfile = { ...billingProfile };
      updatedProfile.Email = updatedEmail;
      setUpdateProfileError(undefined);
      updateBillingProfile(updatedProfile)
        .then(() => {
          setUpdatedEmail('');
          setIsInEditMode(false);
        })
        .catch(error => {
          // Assume that any error is due to the email being invalid
          setUpdateProfileError(error);
        });
    }
  }, [canManageBilling, billingProfile, updateBillingProfile, updatedEmail]);

  return (
    <StyledBody>
      {isInEditMode ? (
        <EditContainer>
          <EditTitleContainer>
            <StyledHeader>Email</StyledHeader>
            <ButtonV2
              size="middle"
              shape="round"
              type="secondary"
              dataCy="discard-email-changes"
              style={CloseCircleButtonStyle}
              onClick={closeEditMode}
            >
              <Icon icon={'closeOutlined'} size={16} color="black" />
            </ButtonV2>
          </EditTitleContainer>
          {updateProfileError && (
            <StyledFormStatus
              status="error"
              text={updateProfileError.message}
            />
          )}
          <Input
            onChange={setUpdatedEmail}
            placeholder="Email"
            value={updatedEmail}
            disabled={isFetchingProfile || isUpdatingProfile}
            required
            width="50%"
            error={
              isValidEmail(updatedEmail) ? '' : 'Please enter a valid email'
            }
            dataCy="email-input-field"
          />
          <Spacer />
          <ButtonV2
            size="middle"
            disabled={
              !updatedEmail || isUpdatingProfile || !isValidEmail(updatedEmail)
            }
            dataCy="save-button"
            style={SaveButtonWidth}
            onClick={updateEmail}
          >
            {isUpdatingProfile ? <Spin size="small" /> : 'Save'}
          </ButtonV2>
        </EditContainer>
      ) : (
        <>
          <DetailsContainer>
            <StyledHeader>Email</StyledHeader>
            {billingProfile?.Email ? (
              <StyledDescription>{billingProfile.Email}</StyledDescription>
            ) : (
              <StyledDescription>Not provided</StyledDescription>
            )}
          </DetailsContainer>

          <StyledButton
            onClick={switchToEditMode}
            color="black"
            disabled={!canManageBilling.response || !hasSavedCard}
          >
            {isUpdatingProfile ? <Spin size="small" /> : 'Update'}
          </StyledButton>
        </>
      )}
    </StyledBody>
  );
};

export default BillingEmailCard;

const StyledFormStatus = styled(FormStatus)`
  margin: 16px 0;
`;

const StyledButton = styled(Button)`
  &:disabled {
    background-color: ${Color.LIGHTER_GREY};
    color: ${Color.BORDER_GREY};
  }
`;
